import React, { useContext } from 'react';
import styled from 'styled-components';
import get from 'lodash/get';
import set from 'lodash/set';
import filter from 'lodash/filter';
import cloneDeep from 'lodash/cloneDeep';

import { EditorSection, StyledOptionsSection } from 'src/presentation/components/pages/shared/EditorSectionLayout';
import FormContext from 'src/presentation/contexts/FormContext';
import generateId from 'src/lib/generateId';
import InputField from 'src/presentation/components/forms/InputField';
import { AttrToRoleMapping, AttrToInboxMapping } from 'src/actions/integrations/sso/ssoConfigResource';
import { DomainError } from 'src/models/DomainError';
import MappingToSelectorField from './MappingToSelectorField';

type MappingConfig = {
  addButtonLabel: string;
  dropdownPlaceholder: string;
  mappingToColumnLabel: string;
  mappingToFieldName: string;
};

type MappingItem = (AttrToRoleMapping | AttrToInboxMapping) & { id: string };

type AttributeMappingsProps = {
  attr: string;
  options: Array<{ label: string; value: string }>;
  config: MappingConfig;
};

export const AttributeMappings = ({ attr, options, config }: AttributeMappingsProps) => {
  const { workingState, errors, updateState, resetError } = useContext(FormContext);

  const getNewItemDefaultData = () => {
    return {
      id: generateId(),
      attributeKey: '',
      attributeValue: '',
      [config.mappingToFieldName]: [],
    };
  };

  const handleDelete = (id: string, index: number) => {
    const items: MappingItem[] = get(workingState, attr, []);
    const clonedItems = cloneDeep(items);
    const filteredItems = filter(clonedItems, (item: MappingItem) => item.id !== id);
    const updatedState = set({}, attr, filteredItems);
    updateState(updatedState, { replaceArrayValues: true });

    const itemAttr = `${attr}[${index}]`;
    const errorsToReset = errors.filter((error: DomainError) => error.attr?.startsWith(itemAttr));
    errorsToReset.forEach(({ attr }: DomainError) => resetError({ attr, errors }));
  };

  const renderItem = ({ index }: { index: number }) => {
    return <AttributesMappingItem attr={`${attr}[${index}]`} config={config} options={options} />;
  };

  return (
    <StyledEditorSection
      addButtonLabel={config.addButtonLabel}
      attr={attr}
      className="mappings-section"
      data-aid={attr}
      onDelete={handleDelete}
      getNewItemDefaultData={getNewItemDefaultData}
      renderItem={renderItem}
      tableHeader={
        <EditingMappingContainer>
          <InputContainer>SAML Attribute</InputContainer>
          <InputContainer>SAML Attribute Value</InputContainer>
          <InputContainer>{config.mappingToColumnLabel}</InputContainer>
        </EditingMappingContainer>
      }
    />
  );
};

type AttributesMappingItemProps = {
  attr: string;
  options: Array<{ label: string; value: string }>;
  config: MappingConfig;
};

const AttributesMappingItem = ({ attr, options, config }: AttributesMappingItemProps) => {
  const { workingState, errors } = useContext(FormContext);

  const mapping = get(workingState, attr);
  const fieldErrors = errors.find(error => error.attr === attr) || {};
  const errorMessage = fieldErrors ? fieldErrors.detail : null;

  if (!mapping) return null;

  return (
    <>
      <EditingMappingContainer>
        <InputContainer>
          <StyledInputField
            attr={`${attr}.attributeKey`}
            className={errorMessage ? 'invalid' : '1'}
            data-aid={`${attr}.attributeKey`}
            placeholder="e.g., Department"
            $invalid={!!errorMessage}
          />
        </InputContainer>

        <InputContainer>
          <StyledInputField
            attr={`${attr}.attributeValue`}
            className={errorMessage ? 'invalid' : '1'}
            data-aid={`${attr}.attributeValue`}
            placeholder="e.g., Support"
            $invalid={!!errorMessage}
          />
        </InputContainer>

        <MultiSelectDropdownContainer>
          <MappingToSelectorField
            attr={`${attr}.${config.mappingToFieldName}`}
            data-aid={`${attr}.${config.mappingToFieldName}`}
            placeholder={config.dropdownPlaceholder}
            options={options}
            label=""
            description=""
          />
        </MultiSelectDropdownContainer>
      </EditingMappingContainer>
      {errorMessage && <ErrorMessage data-aid={`${attr}-error`}>{errorMessage}</ErrorMessage>}
    </>
  );
};

const StyledInputField = styled(InputField)<{ $invalid?: boolean }>`
  & > div:last-child > div {
    border: 1px solid ${p => (p.$invalid ? p.theme.colors.red500 : p.theme.colors.gray300)};
  }

  input {
    width: 100%;
  }
`;

const StyledEditorSection = styled(EditorSection)`
  ${StyledOptionsSection} {
    background-color: ${p => p.theme.colors.white};
    border: 0;
  }

  table {
    tbody tr:first-child {
      td {
        font-weight: ${p => p.theme.fontWeight.heavy};
        border-bottom: 1px solid ${p => p.theme.colors.gray300};
      }
    }
  }
`;

const MultiSelectDropdownContainer = styled.div`
  width: 180px;
`;

const InputContainer = styled.div`
  width: 150px;
`;

const EditingMappingContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 3fr;
  gap: 16px;
  flex: 1;
  width: 100%;
  overflow: hidden;
`;

const ErrorMessage = styled.div`
  color: ${p => p.theme.colors.red500};
  font-size: 12px;
  margin-top: 4px;
  font-weight: ${p => p.theme.fontWeight.normal || '400'};
`;
